import { linkOptions } from '@tanstack/react-router';

import { SupplierMessage } from '../../../../../types/SupplierMessage';
import { TableBuilder } from '../../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../../cdl/TableBuilder/TableBuilderColumn';
import { GenericError } from '../../../../common/Error/GenericError';
import { formatCompanyName } from '../../../../common/helpers/formatCompanyName.helper';
import { formatDateTime } from '../../../../common/helpers/formatDateTime.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { useRole } from '../../../../common/hooks/useRole';
import { IconCheck } from '../../../../common/icons/cdl/Check';
import { LoadingIndicator } from '../../../../common/LoadingIndicator/LoadingIndicator';
import { TableEmptyState } from '../../../../common/TableEmptyState/TableEmptyState';
import { useSupplierList } from '../hooks/useSupplierList';

interface SuppliersTableProps {
    groupId: string;
}

export const SuppliersTable = ({ groupId }: SuppliersTableProps) => {
    const suppliersQuery = useSupplierList({ groupId });
    const role = useRole();

    if (suppliersQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (suppliersQuery.isError) {
        return <GenericError />;
    }

    if (!suppliersQuery.data.length) {
        return <TableEmptyState text={translate('suppliers.emptylist')} />;
    }

    const reversedSuppliers = [...suppliersQuery.data].reverse();

    return (
        <TableBuilder
            data={reversedSuppliers}
            rowLink={(row: SupplierMessage) => {
                return linkOptions({ to: '/supplier/detail/$id', params: { id: row.id } });
            }}
            grow={false}
        >
            <TableBuilderColumn<SupplierMessage> header={translate('customer.name')} width="30%" truncate>
                {(row) => formatCompanyName({ company: row, includeType: role.isAdmin() })}
            </TableBuilderColumn>
            <TableBuilderColumn<SupplierMessage> header={translate('customer.mail')} width="30%" truncate>
                {(row) => row.email}
            </TableBuilderColumn>
            <TableBuilderColumn<SupplierMessage> header={translate('customer.datecreated')}>
                {(row) => formatDateTime({ date: row.dateCreated })}
            </TableBuilderColumn>
            {role.isAdmin() ? (
                <TableBuilderColumn<SupplierMessage> header={translate('customer.testcompany')}>
                    {(row) => (row.testCompany ? <IconCheck width={16} height={16} /> : null)}
                </TableBuilderColumn>
            ) : null}
        </TableBuilder>
    );
};
